import Phaser from 'phaser';
import type { Palette } from './palette';

/**
 * Procedural wood-plank canvas textures for palettes that ship a `wood` tone
 * set (currently the Picnic crate container). Same pattern as util/glow.ts:
 * bake once into a cached canvas texture, keyed by size + orientation, so
 * repeated scene starts reuse it.
 *
 * The grain is drawn with a tiny seeded LCG instead of Math.random so every
 * wall's pattern is stable across restarts — the crate doesn't "reshuffle"
 * when a run ends.
 */

type WoodTones = NonNullable<Palette['wood']>;

/** Deterministic 0..1 generator, seeded per texture key. */
function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function cssRgb(color: number, alpha: number): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Ensure a wood texture of logical size w × h exists and return its key.
 * `along` is the grain direction: 'y' for the side walls, 'x' for the floor.
 * Drawn at 2× and displayed at logical size for crisp grain lines.
 */
export function ensureWoodTexture(
  scene: Phaser.Scene,
  w: number,
  h: number,
  tones: WoodTones,
  along: 'x' | 'y',
): string {
  const key = `wood-${along}-${Math.round(w)}x${Math.round(h)}-${tones.base.toString(16)}`;
  if (scene.textures.exists(key)) return key;

  const SS = 2;
  const cw = Math.max(2, Math.round(w * SS));
  const ch = Math.max(2, Math.round(h * SS));
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    scene.textures.addCanvas(key, canvas);
    return key;
  }

  // Draw in "plank space": x runs along the grain, y across the thickness.
  // For vertical walls, rotate the canvas so the same code paints both.
  const L = (along === 'x' ? cw : ch);
  const T = (along === 'x' ? ch : cw);
  ctx.save();
  if (along === 'y') {
    ctx.translate(cw, 0);
    ctx.rotate(Math.PI / 2);
  }
  const rand = lcg(L * 7919 + T * 104729 + tones.base);

  // Plank body with a soft top-light → bottom-shade roll for the clay read.
  ctx.fillStyle = cssRgb(tones.base, 1);
  ctx.fillRect(0, 0, L, T);
  ctx.fillStyle = cssRgb(tones.grainLight, 0.45);
  ctx.fillRect(0, 0, L, T * 0.2);
  ctx.fillStyle = cssRgb(tones.grainDark, 0.3);
  ctx.fillRect(0, T * 0.8, L, T * 0.2);

  // Long wavy grain streaks, alternating dark and light.
  const streaks = Math.max(3, Math.round(L / (90 * SS)) + 3);
  for (let i = 0; i < streaks; i++) {
    const dark = i % 2 === 0;
    const y0 = T * (0.18 + 0.64 * rand());
    const amp = T * (0.04 + 0.1 * rand());
    ctx.strokeStyle = cssRgb(dark ? tones.grainDark : tones.grainLight, 0.16 + 0.16 * rand());
    ctx.lineWidth = (0.8 + rand() * 1.4) * SS;
    ctx.beginPath();
    ctx.moveTo(-4, y0);
    const segs = Math.max(2, Math.round(L / (70 * SS)));
    for (let s = 1; s <= segs; s++) {
      const x = (L * s) / segs;
      ctx.quadraticCurveTo(x - L / segs / 2, y0 + (rand() - 0.5) * 2 * amp, x, y0 + (rand() - 0.5) * amp);
    }
    ctx.stroke();
  }

  // Plank joints on long runs (the floor reads as a slatted crate base), each
  // a dark seam with a thin lit edge beside it.
  if (L > 150 * SS) {
    let x = (80 + rand() * 50) * SS;
    while (x < L - 60 * SS) {
      ctx.fillStyle = cssRgb(tones.grainDark, 0.5);
      ctx.fillRect(x, 0, 1.5 * SS, T);
      ctx.fillStyle = cssRgb(tones.grainLight, 0.4);
      ctx.fillRect(x + 1.5 * SS, 0, 1 * SS, T);
      x += (95 + rand() * 60) * SS;
    }
  }

  // A small knot or two on longer planks.
  const knots = L > 220 * SS ? 2 : L > 120 * SS ? 1 : 0;
  for (let i = 0; i < knots; i++) {
    const kx = L * (0.2 + 0.6 * rand());
    const ky = T * (0.3 + 0.4 * rand());
    const kr = (2 + rand() * 2.5) * SS;
    ctx.strokeStyle = cssRgb(tones.grainDark, 0.4);
    ctx.lineWidth = 1 * SS;
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr * 1.5, kr, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = cssRgb(tones.grainDark, 0.3);
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr * 0.6, kr * 0.4, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  // Outline in end-grain brown so each wall reads as a distinct plank.
  ctx.strokeStyle = cssRgb(tones.edge, 0.9);
  ctx.lineWidth = 2 * SS;
  ctx.strokeRect(SS, SS, cw - 2 * SS, ch - 2 * SS);

  scene.textures.addCanvas(key, canvas);
  return key;
}
